import { useMemo } from 'react';
import cm from 'classnames';

import { ConfigFlags, FileConfig } from 'services/file.service';
import { FileStatus, ProjectFile } from 'services/file.service';
import { SelectionMethod } from 'services/story.service';
import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { IntroKeys } from 'components/app/intro/intro-keys';
import { countStoryboardClips, getFileConfigFlags, isFileStatusCompleted } from 'components/app/_helpers/_app.helper';
import { useCssFilter } from 'components/app/_mediator/css-filter/use-css-filter';
import { useCssRotate } from 'components/app/_mediator/css-rotate/use-css-rotate';
import { useCssFilterFile } from 'components/app/_mediator/css-filter/use-css-filter-file';
import { setConfigPanel } from 'components/app/editor/_mediator/editor.slice';
import { useSelectorCb } from 'components/app/selector/_mediator/use-selector-cb';

import { ReactComponent as FileErrIco } from 'assets/ui/broken-file.svg';
import { Loader } from 'components/UI/loader/loader';
import { ConfigIco } from 'components/app/editor/editor-configure/config-ico';
import { SelectorFilesCover } from './selector-files-cover';
import styles from '../_selector-files.module.scss';

interface Props {
  file: ProjectFile;
}

/**
 * Gets file config flags as display labels, with custom mapping for specific flags.
 * Maps highlightSensitivity to "Auto Detect" for better UX.
 * Hides highlightSensitivity when selection method is Manual.
 */
const getFileConfigDisplayLabels = (config: FileConfig, selectionMethod?: SelectionMethod): string[] => {
  const flags = getFileConfigFlags(config);
  return flags
    .filter((flagName) => {
      // Hide highlightSensitivity when selection method is Manual
      if (flagName === 'highlightSensitivity' && selectionMethod === SelectionMethod.manual) {
        return false;
      }
      return true;
    })
    .map((flagName) => {
      // Map highlightSensitivity to "Auto Detect"
      if (flagName === 'highlightSensitivity') {
        return 'Auto Detect';
      }
      return flagName;
    });
};

export const SelectorFilesElement = ({ file }: Props) => {
  const { srSelectFile } = useSelectorCb();
  const selectedFile = useAppSelector((state) => state.projects.selectedFile);
  const configs = useAppSelector((state) => state.app__files.configs);
  const statuses = useAppSelector((state) => state.app__files.statuses);
  const configPanel = useAppSelector((state) => state.editor.configPanel);
  const selector = useAppSelector((state) => state.editor.selector);
  const clipsMap = useAppSelector((state) => state.editor.clipsMap);
  const storyConfig = useAppSelector((state) => state.editor.storyConfig);
  const dispatch = useAppDispatch();

  const brightness = useCssFilterFile('brightness', file.id);
  const contrast = useCssFilterFile('contrast', file.id);
  const ref = useCssFilter({ brightness, contrast });

  useCssRotate(ref, { rotate: configs[file.id].rotate, flip: configs[file.id]?.flags.includes(ConfigFlags.flip) });

  const isActive = useMemo(() => {
    return selectedFile === file.id;
  }, [selectedFile, file.id]);

  const clipsNum = useMemo(() => {
    const fileClips = selector?.[file.id];
    if (!fileClips || !clipsMap) return undefined;
    return countStoryboardClips(fileClips, clipsMap);
  }, [selector, file, clipsMap]);

  const configFlags = useMemo(() => {
    return getFileConfigDisplayLabels(configs[file.id], storyConfig?.selectionMethod);
  }, [file, configs, storyConfig?.selectionMethod]);

  const isProcessing = useMemo(() => {
    return !isFileStatusCompleted(statuses[file.id]);
  }, [statuses[file.id]]);

  return (
    <div
      className={cm(styles.File, {
        [styles['File--active']]: isActive,
        [styles['File--editing']]: isActive && configPanel === 'file',
        [styles['file--empty']]: clipsNum === 0,
        [styles['file--err']]: file.status === FileStatus.Error,
      })}
      data-intro={IntroKeys.srFile}
      onClick={() => srSelectFile(file.id)}
    >
      <p className={styles.File__title}>{file.fileName}</p>
      <div className={cm(styles.File__wrapper)}>
        <div className={styles.File__err}>
          <FileErrIco />
        </div>
        <div className={styles.File__labels}>
          {clipsNum !== undefined && (
            <div>
              {clipsNum} highlight{clipsNum !== 1 ? 's' : ''}
            </div>
          )}
          {configFlags.map((el, index) => (
            <div key={el + index} className={styles.File__labels_tags}>
              {el}
            </div>
          ))}
          {isProcessing && (
            <div className={styles.File__loader}>
              Processing
              <Loader />
            </div>
          )}
        </div>
        <div className={styles.File__preview}>
          <div ref={ref}>
            <SelectorFilesCover file={file} />
          </div>
        </div>
        {isActive && (
          <ConfigIco
            className={styles.File__config}
            onClick={() => dispatch(setConfigPanel(configPanel !== 'file' ? 'file' : null))}
          />
        )}
      </div>
    </div>
  );
};
